/**
 * Warehouse Checker
 * Checks if products in cart are from the same warehouse before adding to cart
 */

class WarehouseChecker {
  constructor() {
    this.WAREHOUSE_TAG = 'China Warehouse';
  }

  /**
   * Check if a product has the China Warehouse tag
   */
  hasWarehouseTag(tags) {
    if (!tags) return false;

    if (!Array.isArray(tags)) {
      // Handle string format
      if (typeof tags === 'string') {
        tags = tags.split(',').map(tag => tag.trim());
      } else {
        return false;
      }
    }
    return tags.some(tag => tag === this.WAREHOUSE_TAG);
  }

  /**
   * Get current cart data
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      console.error('[WarehouseChecker] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Get product details including tags by handle
   */
  async getProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }
      return await response.json();
    } catch (error) {
      console.error('[WarehouseChecker] Error fetching product:', error);
      return null;
    }
  }

  /**
   * Check if adding product will mix warehouses
   * Returns { canAdd: boolean, message: string }
   */
  async checkWarehouseCompatibility(productTags) {
    const cart = await this.getCart();

    console.log('[WarehouseChecker] Checking compatibility...');
    console.log('[WarehouseChecker] Product tags:', productTags);
    console.log('[WarehouseChecker] Cart items:', cart?.items?.length || 0);

    if (!cart || !cart.items || cart.items.length === 0) {
      // Cart is empty, can add any product
      console.log('[WarehouseChecker] Cart is empty, allowing add');
      return { canAdd: true, message: '' };
    }

    const currentProductHasWarehouse = this.hasWarehouseTag(productTags);
    console.log('[WarehouseChecker] Current product has China Warehouse:', currentProductHasWarehouse);

    // Check warehouse status of all items in cart
    let hasKoreaWarehouse = false;
    let hasChinaWarehouse = false;

    for (const item of cart.items) {
      console.log('[WarehouseChecker] Checking cart item:', item.product_title, 'handle:', item.handle);

      // Fetch product details to get tags
      const productDetails = await this.getProductByHandle(item.handle);
      const cartItemTags = productDetails?.tags || [];
      const cartItemHasWarehouse = this.hasWarehouseTag(cartItemTags);

      console.log('[WarehouseChecker] Cart item tags:', cartItemTags);
      console.log('[WarehouseChecker] Cart item has China Warehouse:', cartItemHasWarehouse);

      if (cartItemHasWarehouse) {
        hasChinaWarehouse = true;
      } else {
        hasKoreaWarehouse = true;
      }
    }

    console.log('[WarehouseChecker] Cart status - Korea:', hasKoreaWarehouse, 'China:', hasChinaWarehouse);

    // If cart already has both warehouses, allow any product (user already accepted mixed cart)
    if (hasKoreaWarehouse && hasChinaWarehouse) {
      console.log('[WarehouseChecker] ✅ Cart already has mixed warehouses, allowing add');
      return { canAdd: true, message: '' };
    }

    // If cart has only one type of warehouse, check if current product would mix them
    if (currentProductHasWarehouse && hasKoreaWarehouse) {
      // Adding China product to Korea-only cart
      console.log('[WarehouseChecker] ⚠️ Adding China product to Korea-only cart');
      const message = 'Your cart contains products from a different warehouse. We recommend placing products from different countries in separate orders to save on shipping costs.';
      return { canAdd: false, message: message };
    }

    if (!currentProductHasWarehouse && hasChinaWarehouse) {
      // Adding Korea product to China-only cart
      console.log('[WarehouseChecker] ⚠️ Adding Korea product to China-only cart');
      const message = 'Your cart contains products from a different warehouse. We recommend placing products from different countries in separate orders to save on shipping costs.';
      return { canAdd: false, message: message };
    }

    console.log('[WarehouseChecker] ✅ Same warehouse, allowing add');
    return { canAdd: true, message: '' };
  }

  /**
   * Show confirmation dialog
   */
  showWarningDialog(message, onConfirm, onCancel) {
    // Remove any existing modals first
    const existingBackdrop = document.querySelector('.warehouse-modal-backdrop');
    if (existingBackdrop && existingBackdrop.parentNode) {
      existingBackdrop.parentNode.removeChild(existingBackdrop);
    }

    // Create modal backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'warehouse-modal-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Create modal
    const modal = document.createElement('div');
    modal.className = 'warehouse-modal';
    modal.style.cssText = `
      background: white;
      padding: 30px;
      border-radius: 8px;
      max-width: 500px;
      width: 90%;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    modal.innerHTML = `
      <div class="warehouse-modal-content">
        <div class="warehouse-modal-icon" style="text-align: center; margin-bottom: 20px;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="margin: 0 0 15px 0; font-size: 20px; font-weight: 600; text-align: center;">Different Warehouse Notice</h2>
        <p style="margin: 0 0 25px 0; line-height: 1.6; color: #666; text-align: center;">${message}</p>
        <div class="warehouse-modal-buttons" style="display: flex; gap: 10px; justify-content: center;">
          <button class="warehouse-modal-cancel" style="
            padding: 12px 24px;
            border: 1px solid #ddd;
            background: white;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s;
          ">Cancel</button>
          <button class="warehouse-modal-confirm" style="
            padding: 12px 24px;
            border: none;
            background: #3b82f6;
            color: white;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s;
          ">Add Anyway</button>
        </div>
      </div>
    `;

    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    // Add hover effects
    const cancelBtn = modal.querySelector('.warehouse-modal-cancel');
    const confirmBtn = modal.querySelector('.warehouse-modal-confirm');

    cancelBtn.addEventListener('mouseenter', () => {
      cancelBtn.style.background = '#f3f4f6';
    });
    cancelBtn.addEventListener('mouseleave', () => {
      cancelBtn.style.background = 'white';
    });

    confirmBtn.addEventListener('mouseenter', () => {
      confirmBtn.style.background = '#2563eb';
    });
    confirmBtn.addEventListener('mouseleave', () => {
      confirmBtn.style.background = '#3b82f6';
    });

    // Handle button clicks
    let modalClosed = false;
    const closeModal = (callback) => {
      if (modalClosed) return;
      modalClosed = true;
      if (backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }
      if (callback) callback();
    };

    cancelBtn.addEventListener('click', () => {
      closeModal(onCancel);
    });

    confirmBtn.addEventListener('click', () => {
      closeModal(onConfirm);
    });

    // Close on backdrop click
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        closeModal(onCancel);
      }
    });
  }
}

// Export for use in other scripts
window.WarehouseChecker = WarehouseChecker;
